import type { AdventureManifest } from "../types/adventure.js";
import { setAdventureDifficultyOverrides } from "../combat/difficulty.js";

/**
 * Module-singleton holding the currently mounted adventure's manifest and
 * base URL. Loaders read from it instead of hardcoding `/<id>/...`
 * paths, so a future adventure picker (Z04) can swap adventures at runtime.
 *
 * Before the boot sequence calls `mountAdventure`, a built-in default
 * points at the v1 bundle (`/bkcrawl-mvp`). This keeps unit
 * tests — which load maps directly without booting the app — working
 * without per-test setup.
 */

const DEFAULT_BASE_URL = "/bkcrawl-mvp";

const DEFAULT_ASSET_ROOTS = {
  maps: "maps",
  ink: "ink",
  sprites: "sprites",
  textures: "textures",
  data: "data",
} as const;

export interface AdventureContext {
  readonly manifest: AdventureManifest;
  readonly baseUrl: string;
}

/** Default fallback used before any manifest has been mounted. */
const DEFAULT_CONTEXT: AdventureContext = {
  baseUrl: DEFAULT_BASE_URL,
  manifest: {
    id: "bkcrawl-mvp",
    title: "bkcrawl MVP",
    author: "Anthropic",
    version: "0.0.0",
    engineVersion: "0.1",
    entryMap: "floor_01",
  },
};

let current: AdventureContext = DEFAULT_CONTEXT;

/** Return the currently-active adventure context. */
export function getActiveAdventure(): AdventureContext {
  return current;
}

/**
 * Replace the active adventure. Intended for the boot path (after fetching
 * the manifest) and adventure-picker handoffs. Tests that don't exercise
 * platformification can leave the default context alone.
 */
export function setActiveAdventure(ctx: AdventureContext): void {
  current = ctx;
  setAdventureDifficultyOverrides(ctx.manifest.difficulty);
}

/** Reset to the built-in default. Intended for test teardown. */
export function resetActiveAdventure(): void {
  current = DEFAULT_CONTEXT;
  setAdventureDifficultyOverrides(undefined);
}

// ── URL builders (used by loaders) ───────────────────────────────────────────

function assetRoot(key: keyof typeof DEFAULT_ASSET_ROOTS): string {
  const overlay = current.manifest.assetRoots;
  if (overlay && overlay[key]) return overlay[key]!;
  return DEFAULT_ASSET_ROOTS[key];
}

export function mapUrl(mapId: string): string {
  return `${current.baseUrl}/${assetRoot("maps")}/${mapId}.tmj`;
}

export function inkUrl(mapId: string): string {
  return `${current.baseUrl}/${assetRoot("ink")}/${mapId}.ink.json`;
}

/** Shop record for an NPC (EV-E6): `<base>/<data>/shops/<id>.json`. */
export function shopUrl(shopkeeperId: string): string {
  return `${current.baseUrl}/${assetRoot("data")}/shops/${shopkeeperId}.json`;
}

/** Adventure pregen roster (EV-E8): `<base>/<data>/party/pregens.json`. */
export function pregensUrl(): string {
  return `${current.baseUrl}/${assetRoot("data")}/party/pregens.json`;
}

/** New-game intro knot from the manifest (null when the adventure has none). */
export function introKnot(): string | null {
  return current.manifest.introKnot ?? null;
}

/** EV-E9 — display currency name ("Gold" unless the manifest overrides). */
export function currencyName(): string {
  return current.manifest.currency?.name ?? "Gold";
}

/** EV-E9 — display currency abbreviation ("gp" unless overridden). */
export function currencyAbbrev(): string {
  return current.manifest.currency?.abbrev ?? "gp";
}

/** EV-E10 — the word for a single spell ("spell" / "power" / …). */
export function termSpell(): string {
  return current.manifest.terms?.spell ?? "spell";
}

/** EV-E10 — the plural ("spells" / "powers" / …). */
export function termSpells(): string {
  return current.manifest.terms?.spells ?? "spells";
}

export function spriteUrl(entityId: string): string {
  return `${current.baseUrl}/${assetRoot("sprites")}/${entityId}.png`;
}

/**
 * URLs for an item's two-image sprite pair (A05).
 *
 * Adventure-scoped under `<base>/items/<id>/{icon,floor}.png`. The
 * adventure ships its own copy of every item sprite it references —
 * there is no cross-adventure fallback. `ItemSpriteResolver` returns
 * `null` when the file is missing; UI consumers render a procedural
 * letter-tile.
 */
export function itemIconUrl(itemId: string): string {
  return `${current.baseUrl}/items/${itemId}/icon.png`;
}

export function itemFloorUrl(itemId: string): string {
  return `${current.baseUrl}/items/${itemId}/floor.png`;
}

export function itemManifestUrl(itemId: string): string {
  return `${current.baseUrl}/items/${itemId}/item.json`;
}

/**
 * URL for a world-surface PBR texture map (W20).
 *
 * `surface` names the architectural class (e.g. "floor", "wall",
 * "ceiling", "door"); `kind` is one of `albedo`, `normal`, `roughness`.
 * Authored assets live under
 * `<adventureBase>/world/textures/<surface>/<kind>.png`. Missing files
 * are tolerated by `MaterialFactory` — see W20 in
 * `specs/world-objects-and-props.md`.
 */
/**
 * URL for an authored world-object PNG (W18/W19).
 *
 * `klass` is the orientation class (e.g. "floor-decals", "props", "doors"),
 * `objectId` the per-object slug, `state` the variant ("idle", "armed",
 * "open-full", etc.). Mirrors `spriteUrl` for creature sprites; missing
 * files fall back to procedural canvas textures in the renderer.
 */
export function worldObjectUrl(klass: string, objectId: string, state: string): string {
  return `${current.baseUrl}/world/${klass}/${objectId}/${state}.png`;
}

/**
 * URL for the adventure's authored door skin (one skin per adventure).
 * Missing files fall back to the procedural canvas doors in
 * `doorTextures.ts` — see `DoorRenderer`.
 */
export function doorTextureUrl(state: "closed" | "open"): string {
  return `${current.baseUrl}/world/doors/${state}.png`;
}

export function worldTextureUrl(
  surface: string,
  kind: "albedo" | "normal" | "roughness",
  setKey?: string,
): string {
  if (setKey) {
    return `${current.baseUrl}/world/textures/sets/${setKey}/${surface}/${kind}.png`;
  }
  return `${current.baseUrl}/world/textures/${surface}/${kind}.png`;
}

// ── Manifest loader ──────────────────────────────────────────────────────────

/**
 * Fetch `<baseUrl>/manifest.json`, set it as the active adventure, and return
 * the resolved context. Called once at boot before any map/Ink load.
 */
export async function mountAdventure(baseUrl: string): Promise<AdventureContext> {
  const trimmed = baseUrl.replace(/\/+$/, "");
  const res = await fetch(`${trimmed}/manifest.json`);
  if (!res.ok) {
    throw new Error(
      `Failed to load adventure manifest from ${trimmed}/manifest.json: HTTP ${String(res.status)}`,
    );
  }
  const manifest = (await res.json()) as AdventureManifest;
  const ctx: AdventureContext = { manifest, baseUrl: trimmed };
  setActiveAdventure(ctx);
  return ctx;
}
